/**
 * The inside of a storage menu item — toolbar More menu and row context menu alike (UI/UX fix round A, reviews A4 /
 * U16): the icon (as the SQL grid menus show one), the label, the reason an unavailable item cannot run as a visible
 * second line (not only a hover title), and the key the grid binds to the action. The item element itself (Radix
 * `DropdownMenuItem` / `ContextMenuItem`, made inert with `reasonMenuItemProps`) belongs to the caller.
 */
import type { ComponentType } from 'react';
import { cn } from '@/lib/utils';

export type StorageMenuItemContentProps = {
  icon?: ComponentType<{ className?: string }>;
  label: string;
  reason?: string;
  shortcut?: string;
  destructive?: boolean;
};

/**
 * C1 (N7): an unavailable item mutes its icon and label only; its reason line stays readable (never item-level opacity).
 * The item element carries the shared `REASON_MENU_ITEM_CLASS` (merge C1+C2: one rule for every menu), which already
 * dims the icon (`[&_svg]:opacity-60`), so the icon here only takes the muted colour.
 */
export function StorageMenuItemContent({ icon: Icon, label, reason, shortcut, destructive }: StorageMenuItemContentProps) {
  return (
    <span className="flex w-full items-start gap-2">
      {Icon ? <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', reason ? 'text-muted-foreground' : destructive && 'text-destructive')} aria-hidden="true" /> : <span className="w-3.5 shrink-0" aria-hidden="true" />}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn(reason ? 'text-muted-foreground' : destructive && 'text-destructive')}>{label}</span>
        {/* Z4: prose wraps at spaces; only an unbroken token (a permission path) breaks where it must. */}
        {reason && <span className="text-xs leading-snug text-muted-foreground [overflow-wrap:anywhere]" data-testid="storage-menu-item-reason">{reason}</span>}
      </span>
      {shortcut && <span className={cn('ml-3 shrink-0 text-xs tracking-wide text-muted-foreground', reason && 'opacity-60')}>{shortcut}</span>}
    </span>
  );
}
